import * as Yup from "yup";
import INPUTS_CONST from "../constants/inputs";

const {
  email,
  validateOtp,
  emailConfirm,
  password,
  passwordConfirm,
  eligibility: { orgType, orgRegionState, orgRegionId },
  orgInfo: {
    orgName,
    orgAddress1,
    orgAddress2,
    orgCity,
    orgState,
    orgZip,
    orgWebsite,
    orgRelation,
    orgBankContactName,
    orgBoardMembers,
    orgIs501c3,
    orgIs501c4,
  },
  contactInfo: {
    salutation,
    firstName,
    lastName,
    middleInitial,
    jobTitle,
    phone,
  },
  event: {
    eventName,
    eventCategory,
    eventAmount,
    eventAge,
    eventAttendance,
    eventConstituents,
  },
  sponsors: { sponsors, sponsorName, sponsorCategory, sponsorSinceYear },
  locationsAndDates: {
    locationsAndDates,
    locationAndDateTitle,
    locationAndDateFrequency,
    locationAndDateCity,
    locationAndDateState,
    locationAndDateStart,
    locationAndDateEnd,
  },
  techHelp: { issueType, issueDescription },
  emulate,
  opportunityDescription,
} = INPUTS_CONST;
const phoneRegExp = /^(\(|)\d{3}([-.]|)(\)|)\s{0,1}\d{3}([-.]|)\d{4}$/;
const passwordRegExp = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z]{8,}$/;
const passwordRegExpNew =
  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[~`!@#$%^&*()_+={\[}\]|\\:;"'<,>.?\/-])[A-Za-z\d~`!@#$%^&*()_+={\[}\]|\\:;"'<,>.?\/-]{12,}$/;

const zipCodeRegExp = /^[0-9]{5}(?:-[0-9]{4})?$/;
const eventAmountRegExp = /^\$?((\d,?){0,12}|(\d,?){0,10}\.\d{2})$/; //^\$?(\d{1,3},?)+(\.\d{2}|)$/;
const isRequiredText = (inputName) => `${inputName} is a required field.`;
const integerRegExp = /^(\d,?){0,10}$/;
const numberRegExp = /^\d+$/;

const inputValidationMap = {
  [email.name]: Yup.string()
    .email("Invalid email")
    .max(100, "Invalid email")
    .required(isRequiredText(email.name)),
  [emailConfirm.name]: Yup.string()
    .oneOf([Yup.ref(email.name), null], "Email addresses do not match")
    .required(isRequiredText(emailConfirm.name)),
  [password.name]: Yup.string()
    .min(8, "Invalid password")
    .max(20, "Invalid password")
    .matches(passwordRegExp, "Invalid password")
    .required(isRequiredText(password.name)),
  [passwordConfirm.name]: Yup.string()
    .oneOf([Yup.ref(password.name), null], "Passwords do not match")
    .required(isRequiredText(passwordConfirm.name)),
};

const inputValidationMapNew = {
  [password.name]: Yup.string()
    .min(12, "Invalid password")
    .max(50, "Invalid password")
    .matches(passwordRegExpNew, "Invalid password")
    .required(isRequiredText(password.name)),
};

export const loginSchema = Yup.object().shape({
  [email.name]: inputValidationMap[email.name],
  //[password.name]: inputValidationMap[password.name]
});

export const signupSchema = Yup.object().shape({
  [email.name]: inputValidationMap[email.name],
  [emailConfirm.name]: inputValidationMap[emailConfirm.name],
  [password.name]: inputValidationMapNew[password.name],
  [passwordConfirm.name]: inputValidationMap[passwordConfirm.name],
});

export const resetPasswordSchema = Yup.object().shape({
  [email.name]: inputValidationMap[email.name],
});

export const step0Schema = Yup.object().shape({
  [orgType.name]: Yup.string().required(isRequiredText(orgType.label)),
  [orgRegionState.name]: Yup.string().required(
    isRequiredText(orgRegionState.label)
  ),
  [orgRegionId.name]: Yup.string().required(isRequiredText(orgRegionId.label)),
});

export const step1Schema = Yup.object().shape({
  [orgName.name]: Yup.string()
    .max(75, "Invalid Organization Name")
    .required(isRequiredText(orgName.label))
    .trim(),
  [orgAddress1.name]: Yup.string()
    .max(75, "Invalid Organization Address1")
    .required(isRequiredText(orgAddress1.label))
    .trim(),
  [orgAddress2.name]: Yup.string()
    .max(75, "Invalid Organization Address2")
    .trim(),
  [orgCity.name]: Yup.string()
    .max(75, "Invalid Organization City")
    .required(isRequiredText(orgCity.label))
    .trim(),
  [orgState.name]: Yup.string()
    .required(isRequiredText(orgState.label))
    .nullable(),
  [orgZip.name]: Yup.string()
    .matches(zipCodeRegExp, "Zip code is invalid.")
    .required(isRequiredText(orgZip.label)),
  [orgWebsite.name]: Yup.string()
    .max(255, "Invalid Organization Website")
    .required(isRequiredText(orgWebsite.label)),
  //.url('Organization Website must be a valid Url'),
  [orgRelation.name]: Yup.string().required(
    "Organization relationship to Bank of America is a required field. Please select one."
  ),
  [orgBankContactName.name]: Yup.string().max(
    75,
    "Invalid Current bank contact name"
  ),
  // [orgBoardMembers.name]: Yup.string()
  //   .max(255, "Invalid Majority owners or board members")
  //   .required(isRequiredText(orgBoardMembers.label)),
  [orgIs501c3.name]: Yup.string()
    .required(isRequiredText(orgIs501c3.label))
    .nullable(),
  [orgIs501c4.name]: Yup.string().nullable(),
  // [salutation.name]: Yup.string()
  //   .required(isRequiredText('Salutation'))
  //   .nullable(),
  [firstName.name]: Yup.string()
    .required(isRequiredText(firstName.previewLabel))
    .max(75, "Invalid First name")
    .trim(),
  [lastName.name]: Yup.string()
    .required(isRequiredText(lastName.previewLabel))
    .max(75, "Invalid Last name")
    .trim(),
  [middleInitial.name]: Yup.string().max(5, "Maximum length is 5."),
  [jobTitle.name]: Yup.string()
    .max(75, "Invalid Job title")
    .required(isRequiredText(jobTitle.label))
    .trim(),
  [phone.name]: Yup.string()
    .matches(phoneRegExp, "Phone number is not Valid")
    .required(isRequiredText(phone.label)),
});

export const step2Schema = Yup.object().shape({
  [eventName.name]: Yup.string()
    .max(75, "Invalid Official name of sponsorship or event")
    .required(isRequiredText(eventName.label))
    .trim(),
  [eventCategory.name]: Yup.string().required(
    isRequiredText(eventCategory.label)
  ),
  [eventAmount.name]: Yup.string()
    .matches(eventAmountRegExp, `${eventAmount.previewLabel} is not valid`)
    .required(isRequiredText(eventAmount.previewLabel)),
  [eventAge.name]: Yup.string()
    .matches(numberRegExp, `${eventAge.previewLabel} is not valid`)
    .max(3, `${eventAge.previewLabel} is not valid`)
    .required(isRequiredText(eventAge.previewLabel))
    .nullable(),
  [eventAttendance.name]: Yup.string()
    .matches(integerRegExp, `${eventAttendance.previewLabel} is not valid`)
    .required(isRequiredText(eventAttendance.previewLabel)),
  [eventConstituents.name]: Yup.string().required(
    isRequiredText(eventConstituents.label)
  ),
  [sponsors.name]: Yup.string().required("You must add a Sponsor"),
  [locationsAndDates.name]: Yup.string().required(
    "You must add a location and date"
  ),
});

export const step3Schema = Yup.object().shape({
  [opportunityDescription.description.name]: Yup.string().max(
    3000,
    "Opportunity Description can't be longer than 3,000 characters."
  ),
});

export const sponsorsSchema = Yup.object().shape({
  [sponsorName.name]: Yup.string()
    .max(255, "Invalid Sponsor name")
    .required(isRequiredText(sponsorName.label))
    .trim(),
  [sponsorCategory.name]: Yup.string().required(
    isRequiredText(sponsorCategory.label)
  ),
  [sponsorSinceYear.name]: Yup.string()
    //.min(4, "Invalid Sponsor since year")
    .matches(numberRegExp, `${sponsorSinceYear.previewLabel} is not valid`)
    .max(4, `${sponsorSinceYear.previewLabel} is not valid`)
    .required(isRequiredText(sponsorSinceYear.label))
    .trim(),
});

export const locationAndDateSchema = Yup.object().shape({
  [locationAndDateTitle.name]: Yup.string()
    .max(255, "Invalid Event title")
    .required(isRequiredText(locationAndDateTitle.label))
    .trim(),
  [locationAndDateFrequency.name]: Yup.string().required(
    isRequiredText(locationAndDateFrequency.label)
  ),
  [locationAndDateCity.name]: Yup.string()
    .max(75, "Invalid City")
    .required(isRequiredText(locationAndDateCity.label))
    .trim(),
  [locationAndDateState.name]: Yup.string().required(
    isRequiredText(locationAndDateState.label)
  ),
  [locationAndDateStart.name]: Yup.date()
    .required(isRequiredText(locationAndDateStart.label))
    .typeError(isRequiredText(locationAndDateStart.label)),
  [locationAndDateEnd.name]: Yup.date()
    .required(isRequiredText(locationAndDateEnd.label))
    .typeError(isRequiredText(locationAndDateEnd.label))
    .when("DateFrom", (DateFrom, schema) => {
      return DateFrom && DateFrom instanceof Date && !isNaN(DateFrom)
        ? schema.min(DateFrom, "End date should be greater than Start date.")
        : schema;
    }),
});

export const techHelpSchema = Yup.object().shape({
  [email.name]: Yup.string()
    .email("Invalid email")
    .required(isRequiredText(email.name)),
  [issueType.name]: Yup.string().required(isRequiredText(issueType.name)),
  [issueDescription.name]: Yup.string()
    .trim()
    .required(isRequiredText(issueDescription.name)),
});

export const emulateSchema = Yup.object().shape({
  [emulate.name]: Yup.string()
    .email("Invalid email")
    .required(isRequiredText(emulate.label)),
});

export const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .required(isRequiredText(validateOtp.label))
    .matches(/^[0-9]*$/, `${validateOtp.label} must be numeric`)
    .nullable(),
});
